import { useState } from "react";
import { Box, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ProviderOrganizationsEditor from "./ProviderOrganizationsEditor";

/**
 * The "Organizations" column in Manage Practices & Providers: where this provider is employed or
 * affiliated, read from their directory listing by NPI. Next to their practices (where they treat
 * patients), so an admin maintains both for a registered provider from one row.
 *
 * Two cases can't be edited here, and say why instead of showing a broken button:
 * no valid NPI on the account, or an NPI that has no directory listing yet.
 */
export default function ProviderOrganizationsCell({ provider, link, onChanged }) {
  const [open, setOpen] = useState(false);

  if (link === undefined) return <Typography variant="caption" color="text.secondary">Loading…</Typography>;
  if (link === null) return <Typography variant="caption" color="text.secondary">Unavailable</Typography>;

  if (link.status === "NO_NPI") {
    return (
      <Box>
        <Chip size="small" variant="outlined" label="No NPI on file" />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Organizations are matched by NPI.
        </Typography>
      </Box>
    );
  }

  if (link.status === "NOT_IN_DIRECTORY") {
    return (
      <Box>
        <Chip size="small" variant="outlined" color="warning" label="Not in directory" />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          NPI {link.npi} has no directory listing. Add it under Provider Management to list organizations.
        </Typography>
      </Box>
    );
  }

  const close = () => {
    setOpen(false);
    if (onChanged) onChanged(); // pick up whatever was added or removed
  };

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
        {link.organizations.length === 0 ? (
          <Typography variant="caption" color="text.secondary">None listed</Typography>
        ) : (
          link.organizations.map((o, i) => (
            <Chip key={`${o.name}-${i}`} size="small" label={o.name} variant={o.primary ? "filled" : "outlined"} />
          ))
        )}
      </Box>
      <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setOpen(true)}>
        Edit organizations
      </Button>

      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>Organizations: {provider.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Where this provider is employed or affiliated, from their directory listing (NPI {link.npi}). Patients see
            these on the search card. This does not change which patients the provider can see; that is set by their
            practices in the column to the left.
          </Typography>
          <ProviderOrganizationsEditor npi={link.npi} primaryName={link.mainOrganization} />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Done</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
